import {createContext, Context, useState, useCallback, useLayoutEffect} from 'react';
import {createRoot} from 'react-dom/client';
import 'normalize.css';
import {ThemeProvider} from '@mui/material';
import * as Sentry from '@sentry/react';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {ReactQueryDevtools} from '@tanstack/react-query-devtools';
import {TonConnectUIProvider} from '@tonconnect/ui-react';
import Routes from './routes.tsx';
import theme from './theme/theme.ts';
import {IntlProvider} from 'react-intl';
import Locale from './components/enums/locale.ts';
import enMessages from './shared/locales/en';
import ruMessages from './shared/locales/ru';
import setToLocalStorage from './helpers/local-storage/set-to-local-storage.ts';
import getFromLocalStorage from './helpers/local-storage/get-from-local-storage.ts';

export const RootContext: Context<any> = createContext({
    locale: Locale.EN,
    sendTokenAddress: 'native',
    receiveTokenAddress: null,
});

const RootComponent: React.FunctionComponent = () => {
    const queryClient = new QueryClient();

    const messagesAllLocales: any = {
        [Locale.EN]: enMessages,
        [Locale.RU]: ruMessages,
    };

    const [locale, setLocale] = useState<Locale>(Locale.EN);
    const [messages, setMessages] = useState<any>(messagesAllLocales[locale]);

    useLayoutEffect(() => {
        const localeState: string = getFromLocalStorage('locale');
        if (localeState) {
            setLocale(localeState as Locale);
            setMessages(messagesAllLocales[localeState as Locale]);
        }
    }, []);

    const onLocaleChange = useCallback(
        (locale: Locale) => {
            setToLocalStorage('locale', locale);
            setLocale(locale);
            setMessages(messagesAllLocales[locale]);
        },
        [locale],
    );

    Sentry.init({
      dsn: 'https://20fba3f9793dd52b2df3f8eafda1899f@o4508791229579264.ingest.de.sentry.io/4508791245701200',
      integrations: [],
    });

    return (
        // <StrictMode>
        <QueryClientProvider client={queryClient}>
            <ThemeProvider theme={theme}>
                <RootContext.Provider
                    value={{
                        locale,
                        onLocaleChange,
                    }}
                >
                    <IntlProvider messages={messages} locale="en" defaultLocale="en">
                        <TonConnectUIProvider manifestUrl="https://mybit.me/tonconnect-manifest.json">
                            <>
                                <Routes/>
                                {import.meta.env.MODE !== 'production' && <ReactQueryDevtools initialIsOpen={false}/>}
                            </>
                        </TonConnectUIProvider>
                    </IntlProvider>
                </RootContext.Provider>
            </ThemeProvider>
        </QueryClientProvider>
        // </StrictMode>
    );
};

createRoot(document.querySelector('#root')!).render(<RootComponent/>);
